"use client";
import type { CSSProperties } from "react";
import { createTheme } from "@mui/material/styles";

// Extend the MUI palette so "primary.fill" is type-safe.
declare module "@mui/material/styles" {
    interface PaletteColor { fill: string; }
    interface SimplePaletteColorOptions { fill?: string; }
    interface TypographyVariants { sectionTitle: CSSProperties; }
    interface TypographyVariantsOptions { sectionTitle?: CSSProperties; }

    // Mirrors Figma's `_components/alert/<severity>/<background|color>` tokens.
    interface Palette {
        alert: {
            error: { background: string; color: string };
            warning: { background: string; color: string };
            info: { background: string; color: string };
            success: { background: string; color: string };
        };
    }
    interface PaletteOptions {
        alert?: Palette["alert"];
    }
}
declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides { sectionTitle: true; }
}

import { Parkinsans } from "next/font/google";

export const BRAND_COLOURS = {
  rust: "#f96c48",
} as const;

export const ParkinsansFont = Parkinsans({
  weight: ["300", "400", "500", "600", "700"],
  subsets: ["latin"],
  display: "swap",
  variable: "--font-parkinsans",
  fallback: ["Arial", "Helvetica", "sans-serif"],
});

const theme = createTheme({
  cssVariables: true,
  shape: { borderRadius: 24 },
  typography: {
    fontFamily: ParkinsansFont.style.fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: "2px",
      textTransform: "uppercase",
      display: "block",
      fontFeatureSettings: "'liga' off, 'clig' off",
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#703363",
      light: "#884879",
      dark: "#542a63",
      contrastText: "#ffffff",
      fill: "#f7c7de", // Figma: --_components/avatar/fill (derived from primary.main)
    },
    secondary: {
      main: "#004f63",
      light: "#26667a",
      dark: "#003746",
      contrastText: "#ffffff",
    },
    error: {
      main: "#c8312a",
      dark: "#93000a",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#fae594",
      light: "#fff0bf",
      dark: "#f36944",
      contrastText: "#004f63",
    },
    info: {
      main: "#004f63",
      light: "#15596e",
      dark: "#001f28",
      contrastText: "#b5edf2",
    },
    success: {
      main: "#1e783c",
      dark: "#003916",
      contrastText: "#e7f5ec",
    },
    text: {
      primary: "rgba(0, 79, 99, 0.87)",
      secondary: "rgba(0, 79, 99, 0.60)",
      disabled: "rgba(0, 79, 99, 0.38)",
    },
    background: {
      default: "#f5ede5",
      paper: "#ffffff",
    },
    action: {
      active: "#703363",
      hover: "rgba(112, 56, 131, 0.04)",
      selected: "rgba(112, 56, 131, 0.08)",
      focus: "rgba(112, 56, 131, 0.12)",
      disabled: "rgba(64, 23, 78, 0.26)",
      disabledBackground: "rgba(241, 235, 239, 0.12)",
    },
    divider: "rgba(0, 79, 99, 0.12)",
    // Figma: _components/alert/<severity>/<background|color> — single source of truth for both
    // the MuiAlert overrides below and the Customer Portal announcement banner.
    alert: {
      error: { background: "#fceae9", color: "#c8312a" },
      warning: { background: "#fae594", color: "#004f63" },
      info: { background: "#e5f6fd", color: "#004f63" },
      success: { background: "#e7f5ec", color: "#1e783c" },
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiFilledInput: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiAlert: {
      variants: [
        {
          props: { variant: "standard", severity: "error" },
          style: ({ theme }) => ({
            color: theme.palette.alert.error.color,
            backgroundColor: theme.palette.alert.error.background,
          }),
        },
        {
          props: { variant: "standard", severity: "warning" },
          style: ({ theme }) => ({
            color: theme.palette.alert.warning.color,
            backgroundColor: theme.palette.alert.warning.background,
          }),
        },
        {
          props: { variant: "standard", severity: "success" },
          style: ({ theme }) => ({
            color: theme.palette.alert.success.color,
            backgroundColor: theme.palette.alert.success.background,
          }),
        },
        // No "info" override here on purpose — the two existing plain `severity="info"` Alerts
        // (get-in-touch, login) rely on MUI's default info styling; adding one would silently
        // restyle them. The announcement banner sources `alert.info.*` directly instead.
      ],
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.fill,
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: { backgroundColor: "#f1ebef" },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSuccess: {
          backgroundColor: "rgba(30, 120, 60, 0.3)",
          color: "#003916",
        },
        colorError: {
          backgroundColor: "rgba(200, 49, 42, 0.3)",
          color: "#93000a",
        },
      },
      // Figma: chip outer padding 4px + label padding 6px = 10px per side.
      // MUI medium default is 12px — override via variants.
      variants: [
        {
          props: { size: "medium" },
          style: { "& .MuiChip-label": { paddingLeft: 10, paddingRight: 10 } },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: { sectionTitle: "h2" },
      },
    },
  },
});

export default theme;
